import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

// ── Helpers ───────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function safeParse(data: string): any {
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

// ── Types ─────────────────────────────────────────────────────

export type CoachPhase = 'setup' | 'research' | 'analysis' | 'interview' | 'tailoring' | 'review' | 'export';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

export interface ToolStatus {
  name: string;
  description: string;
  status: 'running' | 'complete' | 'error';
  summary?: string;
}

export interface AskUserPrompt {
  toolCallId: string;
  question: string;
  context: string;
  inputType: 'text' | 'voice' | 'multiple_choice';
  choices?: Array<{ label: string; description?: string }>;
  skipAllowed: boolean;
}

export interface ResumeUpdate {
  section: string;
  content: string;
  changeType: 'rewrite' | 'add' | 'remove';
}

export interface ExportData {
  resume: {
    summary: string;
    experience: Array<{
      company: string;
      title: string;
      start_date: string;
      end_date: string;
      location: string;
      bullets: Array<{ text: string; source: string }>;
    }>;
    skills: Record<string, string[]>;
    education: Array<{
      institution: string;
      degree: string;
      field: string;
      year: string;
    }>;
    certifications: Array<{
      name: string;
      issuer: string;
      year: string;
    }>;
    ats_score: number;
  };
}

export interface SessionState {
  sessionId: string | null;
  phase: CoachPhase;
  messages: ChatMessage[];
  toolStatuses: ToolStatus[];
  askUserPrompt: AskUserPrompt | null;
  resumeUpdates: ResumeUpdate[];
  exportData: ExportData | null;
  isConnected: boolean;
  isProcessing: boolean;
  error: string | null;
}

// ── Agent Server URL ──────────────────────────────────────────

const AGENT_URL = import.meta.env.VITE_AGENT_SERVER_URL || 'http://localhost:3001';

// ── Hook ──────────────────────────────────────────────────────

export function useAgentSession() {
  const { session } = useAuth();
  const eventSourceRef = useRef<EventSource | null>(null);
  const messageIdCounter = useRef(0);

  const [state, setState] = useState<SessionState>({
    sessionId: null,
    phase: 'setup',
    messages: [],
    toolStatuses: [],
    askUserPrompt: null,
    resumeUpdates: [],
    exportData: null,
    isConnected: false,
    isProcessing: false,
    error: null,
  });

  const getAuthHeaders = useCallback(() => {
    if (!session?.access_token) throw new Error('Not authenticated');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
  }, [session]);

  const nextMsgId = useCallback(() => {
    messageIdCounter.current += 1;
    return `msg-${messageIdCounter.current}-${Date.now()}`;
  }, []);

  // ── SSE Connection ────────────────────────────────────────

  const connectSSE = useCallback((sessionId: string) => {
    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // EventSource doesn't support custom headers, so pass token via query param
    const token = session?.access_token;
    if (!token) return;
    const url = `${AGENT_URL}/api/sessions/${sessionId}/sse?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);

    let streamingText = '';

    es.addEventListener('connected', () => {
      setState((s) => ({ ...s, isConnected: true, error: null }));
    });

    es.addEventListener('text_delta', (e) => {
      const data = safeParse(e.data);
      if (!data) return;
      streamingText += data.content;
      setState((s) => {
        const msgs = [...s.messages];
        const lastMsg = msgs[msgs.length - 1];
        if (lastMsg?.role === 'assistant' && lastMsg.id.startsWith('stream-')) {
          // Update streaming message
          msgs[msgs.length - 1] = { ...lastMsg, content: streamingText };
        } else {
          // Start new streaming message
          msgs.push({
            id: `stream-${Date.now()}`,
            role: 'assistant',
            content: streamingText,
            timestamp: new Date(),
          });
        }
        return { ...s, messages: msgs, isProcessing: true };
      });
    });

    es.addEventListener('text_complete', () => {
      // Finalize the streaming message
      streamingText = '';
      setState((s) => {
        const msgs = [...s.messages];
        const lastMsg = msgs[msgs.length - 1];
        if (lastMsg?.role === 'assistant' && lastMsg.id.startsWith('stream-')) {
          msgs[msgs.length - 1] = { ...lastMsg, id: `msg-${Date.now()}` };
        }
        return { ...s, messages: msgs, isProcessing: false };
      });
    });

    es.addEventListener('tool_start', (e) => {
      const data = safeParse(e.data);
      if (!data) return;
      setState((s) => ({
        ...s,
        toolStatuses: [...s.toolStatuses, {
          name: data.tool_name,
          description: data.description,
          status: 'running',
        }],
        isProcessing: true,
      }));
    });

    es.addEventListener('tool_complete', (e) => {
      const data = safeParse(e.data);
      if (!data) return;
      setState((s) => ({
        ...s,
        toolStatuses: s.toolStatuses.map((t) =>
          t.name === data.tool_name && t.status === 'running'
            ? { ...t, status: 'complete' as const, summary: data.summary }
            : t
        ),
      }));
    });

    es.addEventListener('ask_user', (e) => {
      const data = safeParse(e.data);
      if (!data) return;
      streamingText = '';
      setState((s) => ({
        ...s,
        askUserPrompt: {
          toolCallId: data.tool_call_id,
          question: data.question,
          context: data.context,
          inputType: data.input_type,
          choices: data.choices,
          skipAllowed: data.skip_allowed,
        },
        isProcessing: false,
      }));
    });

    es.addEventListener('phase_change', (e) => {
      const data = safeParse(e.data);
      if (!data) return;
      setState((s) => ({
        ...s,
        phase: data.to_phase as CoachPhase,
        messages: [...s.messages, {
          id: nextMsgId(),
          role: 'system',
          content: data.summary,
          timestamp: new Date(),
        }],
      }));
    });

    es.addEventListener('resume_update', (e) => {
      const data = safeParse(e.data);
      if (!data) return;
      setState((s) => ({
        ...s,
        resumeUpdates: [...s.resumeUpdates, {
          section: data.section,
          content: data.content,
          changeType: data.change_type,
        }],
      }));
    });

    es.addEventListener('export_ready', (e) => {
      const data = safeParse(e.data);
      if (!data) return;
      setState((s) => ({
        ...s,
        exportData: { resume: data.resume },
      }));
    });

    es.addEventListener('error', (e) => {
      if (e instanceof MessageEvent) {
        const data = safeParse(e.data);
        if (!data) return;
        setState((s) => ({
          ...s,
          error: data.message,
          isProcessing: false,
        }));
      }
    });

    es.addEventListener('complete', (e) => {
      const data = safeParse(e.data);
      if (!data) return;
      setState((s) => ({
        ...s,
        isProcessing: false,
        messages: [...s.messages, {
          id: nextMsgId(),
          role: 'system',
          content: `Session complete! ATS score: ${data.ats_score}%. ${data.requirements_addressed} requirements addressed, ${data.sections_rewritten} sections rewritten.`,
          timestamp: new Date(),
        }],
      }));
    });

    es.onerror = () => {
      setState((s) => ({ ...s, isConnected: false }));
      // Attempt reconnection after 3s
      setTimeout(() => {
        if (eventSourceRef.current === es) {
          connectSSE(sessionId);
        }
      }, 3000);
    };

    eventSourceRef.current = es;
  }, [session, nextMsgId]);

  // ── Actions ───────────────────────────────────────────────

  const createSession = useCallback(async (masterResumeId?: string, jobApplicationId?: string) => {
    const res = await fetch(`${AGENT_URL}/api/sessions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ master_resume_id: masterResumeId, job_application_id: jobApplicationId }),
    });

    if (!res.ok) throw new Error('Failed to create session');

    const { session: newSession } = await res.json();
    setState((s) => ({ ...s, sessionId: newSession.id }));
    connectSSE(newSession.id);
    return newSession.id;
  }, [getAuthHeaders, connectSSE]);

  const sendMessage = useCallback(async (content: string) => {
    if (!state.sessionId) throw new Error('No active session');

    // Add user message to UI immediately
    setState((s) => ({
      ...s,
      messages: [...s.messages, {
        id: nextMsgId(),
        role: 'user',
        content,
        timestamp: new Date(),
      }],
      askUserPrompt: null,
      isProcessing: true,
      error: null,
    }));

    const res = await fetch(`${AGENT_URL}/api/sessions/${state.sessionId}/messages`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ content }),
    });

    if (!res.ok) {
      setState((s) => ({ ...s, isProcessing: false, error: 'Failed to send message' }));
    }
  }, [state.sessionId, getAuthHeaders, nextMsgId]);

  const resumeSession = useCallback(async (sessionId: string) => {
    const res = await fetch(`${AGENT_URL}/api/sessions/${sessionId}`, {
      headers: getAuthHeaders(),
    });

    if (!res.ok) throw new Error('Session not found');

    const { session: existingSession } = await res.json();
    setState((s) => ({
      ...s,
      sessionId: existingSession.id,
      phase: existingSession.current_phase,
    }));
    connectSSE(existingSession.id);
  }, [getAuthHeaders, connectSSE]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setState((s) => ({ ...s, isConnected: false }));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  return {
    ...state,
    createSession,
    sendMessage,
    resumeSession,
    disconnect,
  };
}
